import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Drawer,
  IconButton,
  Toolbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PlayerSidebar from '@components/layout/PlayerSidebar';
import ContestCatalogExperience from '@components/contest/ContestCatalogExperience';
import { brandColors } from '@config/brandTokens';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { toSportLabel } from '@utils/sports';
import { contestTabFromQuery } from '@components/contest/ContestJourneyChrome';
import { useAuthStore } from '@store/authStore';

const DRAWER_WIDTH = 280;

interface ContestSportPageProps {
  portalMode?: PortalMode;
}

/**
 * Logged-in contest sport page — same 3-frame catalog experience as public,
 * inside the player portal shell (PlayerSidebar). Route unchanged.
 */
const ContestSportPage: React.FC<ContestSportPageProps> = ({ portalMode = 'player' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sport: sportSlug, category: categorySlug = 'all' } = useParams<{
    sport: string;
    category: string;
  }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const paths = getPortalPaths(portalMode);
  const hideSidebar = portalMode === 'coach';

  const sport = toSportLabel(sportSlug);
  const category = (categorySlug || 'all').toLowerCase();
  const ageFromQuery = searchParams.get('age') || undefined;
  const contestFromQuery = searchParams.get('contest');
  const uploadMode = searchParams.get('action') === 'upload';
  const tabFromQuery = contestTabFromQuery(searchParams.get('tab'));

  const playerId = user?.playerId || '';
  const playerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';
  const userId = user?.id;

  const [mobileOpen, setMobileOpen] = useState(false);

  const openUploadForContest = (contestId: string, ageCategory: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'contest');
    params.set('contest', contestId);
    params.set('age', ageCategory);
    params.set('action', 'upload');
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const dismissUploadMode = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('action');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  };

  const handleSelfEvaluate = (ageCategory: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'self-evaluation');
    params.set('age', ageCategory);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  const mainContent = (
    <Box sx={{ px: { xs: 1.25, md: 1.75 }, py: { xs: 1.25, md: 1.5 }, bgcolor: brandColors.ice, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.25 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(paths.contestOverview)}
          sx={{ textTransform: 'none', fontWeight: 700, color: brandColors.navy }}
        >
          Back to contests
        </Button>
      </Box>

      {uploadMode && !playerId && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
          Complete your player profile before uploading a performance video.
        </Alert>
      )}

      <ContestCatalogExperience
        sportName={sport}
        categoryFilter={category}
        initialAgeCategory={ageFromQuery}
        initialTab={tabFromQuery}
        initialContestId={contestFromQuery}
        uploadMode={uploadMode}
        playerId={playerId || undefined}
        playerName={playerName}
        userId={userId}
        frameHeight={{ xs: 'auto', lg: 'calc(100vh - 88px)' }}
        onSubmit={openUploadForContest}
        onSelfEvaluate={handleSelfEvaluate}
        onUploadDismiss={dismissUploadMode}
      />
    </Box>
  );

  if (hideSidebar) {
    return mainContent;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
      >
        <PlayerSidebar />
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
        }}
        open
      >
        <PlayerSidebar />
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, minWidth: 0 }}>
        <Toolbar sx={{ display: { md: 'none' }, minHeight: '48px !important' }}>
          <IconButton onClick={() => setMobileOpen(true)} edge="start" aria-label="Open menu">
            <MenuIcon />
          </IconButton>
        </Toolbar>
        {mainContent}
      </Box>
    </Box>
  );
};

export default ContestSportPage;
